import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import type { ThemeConfig } from '../../api/types';
import { themeIconUrl } from '../../lib/brand';
import { centredScrollLeft } from '../../lib/chrome';
import { nextIndex } from '../../lib/keyboard';

/**
 * The bottom theme bar: one glass card per theme in project order (icon + name; the subtitle shows at phone
 * widths, where the cards go wide and scroll-snap). The active card is lit green. Arrow keys, Home and End move
 * and select.
 */
export function ThemeCarousel({
  themes,
  activeId,
  onSelect,
}: {
  themes: readonly ThemeConfig[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const track = useRef<HTMLUListElement>(null);
  const activeIndex = Math.max(
    0,
    themes.findIndex((t) => t.id === activeId),
  );

  // Rest the active card in view when it changes (only matters when the track overflows, e.g. on a phone).
  useEffect(() => {
    const list = track.current;
    const card = refs.current[activeIndex]?.parentElement;
    if (!list || !card) return;
    const left = centredScrollLeft(
      card.offsetLeft - list.offsetLeft,
      card.offsetWidth,
      list.clientWidth,
      list.scrollWidth,
    );
    if (left !== null) list.scrollLeft = left;
  }, [activeIndex, themes.length]);

  // Which ends of the row have more to scroll to: the row fades out there (more-l / more-r, theme/glass.css).
  useEffect(() => {
    const list = track.current;
    if (!list) return;
    const mark = (): void => {
      list.classList.toggle('more-l', list.scrollLeft > 2);
      list.classList.toggle('more-r', list.scrollLeft + list.clientWidth < list.scrollWidth - 2);
    };
    mark();
    list.addEventListener('scroll', mark, { passive: true });
    const ro = new ResizeObserver(mark);
    ro.observe(list);
    return () => {
      list.removeEventListener('scroll', mark);
      ro.disconnect();
    };
  }, [themes.length]);

  const select = (id: string): void => {
    performance.mark('mm:theme-click');
    onSelect(id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const next = nextIndex(index, e.key, themes.length);
    if (next === null) return;
    e.preventDefault();
    const theme = themes[next];
    if (!theme) return;
    refs.current[next]?.focus();
    select(theme.id);
  };

  if (themes.length < 2) return null;
  return (
    <nav className="mm-btnbar" aria-label="Themes">
      <ul className="mm-btnscroll" ref={track}>
        {themes.map((t, i) => {
          const active = t.id === activeId;
          const icon = themeIconUrl(t.id);
          return (
            <li key={t.id}>
              <button
                ref={(el) => {
                  refs.current[i] = el;
                }}
                type="button"
                className={active ? 'mm-card active' : 'mm-card'}
                aria-current={active ? 'true' : undefined}
                tabIndex={i === activeIndex ? 0 : -1}
                onClick={() => select(t.id)}
                onKeyDown={(e) => onKeyDown(e, i)}
              >
                {icon && (
                  <span className="mm-card-icon" aria-hidden="true">
                    <span style={{ '--mm-icon': `url("${icon}")` } as CSSProperties} />
                  </span>
                )}
                <span className="mm-card-col">
                  <span className="mm-card-name">{t.title}</span>
                  {t.subtitle && <span className="mm-card-sub">{t.subtitle}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
